import { applyMongoFilter } from "@tellescope/utils";
import type { InfiniteData } from "@tanstack/react-query";
import { extractRequestFromQueryKey } from "../../../keys";
import {
  appendToInfiniteDataList,
  convertTicketThreadToInboxThread,
  dedupeInboxThreads,
  removeTicketThreadExcludedInboxThreadFields,
  threadIsNewerThanOldest,
} from "../../../utils";
import { TellescopeModelFromName } from "../../helper-types";
import { modelMatchesGetSomeRequest } from "../filter-utils";
import { CustomModelAutomaticMutationSideEffectConfigs } from "../types";

const DELAY = 3000;

// ---------------------------------------------------------------------------
// Optimistic comment helpers
// ---------------------------------------------------------------------------

/**
 * Prefix used for optimistic ticket thread comment IDs so they can be
 * distinguished from real server-generated IDs.
 */
export const OPTIMISTIC_TICKET_THREAD_COMMENT_ID_PREFIX = "__optimistic_ttc_";

/**
 * Deterministic hash (djb2) of the given string.
 * Returns a hex string — not cryptographically secure, but unique enough
 * to avoid collisions between concurrent optimistic comments.
 */
const djb2Hash = (str: string): string => {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
};

/**
 * Builds a deterministic optimistic ID from the mutation request fields.
 */
const buildOptimisticCommentId = (request: {
  ticketThreadId: string;
  enduserId: string;
  html_body: string;
}): string => {
  const seed = `${request.ticketThreadId}:${request.enduserId}:${request.html_body}`;
  return `${OPTIMISTIC_TICKET_THREAD_COMMENT_ID_PREFIX}${djb2Hash(seed)}`;
};

// ---------------------------------------------------------------------------
// load_threads filter helpers
// ---------------------------------------------------------------------------

/**
 * Fields we can reliably construct for a Zendesk inbox thread from the send_message request.
 * We only keep mdbFilter keys that match these fields so `applyMongoFilter` doesn't
 * fail on fields our partial thread can't know (e.g. archivedAt, trashedAt, readBy, etc.).
 */
const KNOWN_ZENDESK_INBOX_THREAD_KEYS = ["type", "enduserIds"] as const;

const keepOnlyKnownInboxThreadFields = (mdbFilter: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(mdbFilter).filter(([key]) =>
      KNOWN_ZENDESK_INBOX_THREAD_KEYS.includes(key as (typeof KNOWN_ZENDESK_INBOX_THREAD_KEYS)[number])
    )
  );

/**
 * Checks whether a `load_threads` query could be affected by a Zendesk thread
 * belonging to the given enduserId.
 */
const zendeskThreadMatchesLoadThreadsQuery = (
  enduserId: string,
  queryRequest: { mdbFilter?: object; enduserIds?: string[] } | undefined
): boolean => {
  // Check top-level enduserIds param — if the query filters to specific endusers,
  // only invalidate if our enduser is included.
  if (queryRequest?.enduserIds?.length && !queryRequest.enduserIds.includes(enduserId)) {
    return false;
  }

  // Build a partial inbox thread from what we know and test against the known-matchable
  // subset of the mdbFilter. Fields we can't construct (archivedAt, trashedAt, readBy, etc.)
  // are stripped so they don't cause false negatives.
  if (queryRequest?.mdbFilter) {
    const filteredMdbFilter = keepOnlyKnownInboxThreadFields(queryRequest.mdbFilter as Record<string, unknown>);
    const partialThread = {
      type: "Zendesk" as const,
      enduserIds: [enduserId],
    };
    if (!applyMongoFilter(partialThread, filteredMdbFilter)) {
      return false;
    }
  }

  return true;
};

// ---------------------------------------------------------------------------
// Side-effect config
// ---------------------------------------------------------------------------

export const TICKET_THREADS_MUTATION_SIDE_EFFECT_CONFIGS: CustomModelAutomaticMutationSideEffectConfigs<"ticket_threads"> =
  {
    createOne: (_, response) => {
      if (!response) {
        return {};
      }

      const threadFromResponse = convertTicketThreadToInboxThread(response);

      return {
        _otherModels: {
          inbox_threads: {
            load_threads: {
              predicate: (query) => {
                if (!threadIsNewerThanOldest(query.state.data, threadFromResponse)) {
                  return false;
                }

                const request = extractRequestFromQueryKey(query.queryKey);
                const filteredMdbFilter = removeTicketThreadExcludedInboxThreadFields(request?.mdbFilter ?? {});
                return applyMongoFilter(threadFromResponse, filteredMdbFilter);
              },
            },
          },
        },
      };
    },
    createSome: (_, response) => {
      if (!response) {
        return {};
      }

      const threadsFromResponse = dedupeInboxThreads(response.created.map(convertTicketThreadToInboxThread));

      return {
        _otherModels: {
          inbox_threads: {
            load_threads: {
              predicate: (query) => {
                if (threadsFromResponse.every((thread) => !threadIsNewerThanOldest(query.state.data, thread))) {
                  return false;
                }

                const request = extractRequestFromQueryKey(query.queryKey);
                const filteredMdbFilter = removeTicketThreadExcludedInboxThreadFields(request?.mdbFilter ?? {});
                return threadsFromResponse.some((thread) => applyMongoFilter(thread, filteredMdbFilter));
              },
              request: {
                autobuild: true,
              },
              exact: false,
            },
          },
        },
      };
    },
    updateOne: (_, response) => {
      if (!response) {
        return {};
      }

      const threadFromResponse = convertTicketThreadToInboxThread(response);

      return {
        _otherModels: {
          inbox_threads: {
            load_threads: {
              predicate: (query) => {
                // Check if message is already in preview first - if so, always invalidate
                const isInPreview = query.state.data?.pages.some((page) =>
                  page.threads.some((thread) => thread.type === "Email" && thread.threadId === response.id)
                );
                if (isInPreview) {
                  return true;
                }

                // If not in preview, check if it's newer than oldest cached thread
                if (!threadIsNewerThanOldest(query.state.data, threadFromResponse)) {
                  return false;
                }

                // Finally, check if it matches the filter
                const queryRequest = extractRequestFromQueryKey(query.queryKey);
                const filteredMdbFilter = removeTicketThreadExcludedInboxThreadFields(queryRequest?.mdbFilter ?? {});
                return applyMongoFilter(threadFromResponse, filteredMdbFilter);
              },
              request: {
                autobuild: true,
              },
              exact: false,
            },
          },
        },
      };
    },
    deleteOne: (messageIdFromRequest) => ({
      _otherModels: {
        inbox_threads: {
          load_threads: {
            predicate: (query) => {
              const isInPreview = query.state.data?.pages.some((page) =>
                page.threads.some((thread) => thread.type === "Email" && thread.threadId === messageIdFromRequest)
              );

              return !!isInPreview;
            },
            request: {
              autobuild: true,
            },
            exact: false,
          },
        },
      },
    }),
    send_message: (request, response) => {
      // ---------------------------------------------------------------
      // onMutate path (response is undefined) — optimistic updates only
      // ---------------------------------------------------------------
      if (response === undefined) {
        // Only add an optimistic comment when replying to an existing thread
        if (!request.ticketThreadId) return {};

        const now = new Date();
        const optimisticComment: TellescopeModelFromName<"ticket_thread_comments"> = {
          id: buildOptimisticCommentId({
            ticketThreadId: request.ticketThreadId,
            enduserId: request.enduserId,
            html_body: request.html_body,
          }),
          ticketThreadId: request.ticketThreadId,
          enduserId: request.enduserId,
          html: request.html_body,
          plaintext: request.html_body.replace(/<[^>]*>/g, ""),
          public: request.public,
          inbound: false,
          businessId: "",
          updatedAt: now,
          createdAt: now,
          creator: "",
          timestamp: now,
        };

        const partialCommentFromRequest: Partial<TellescopeModelFromName<"ticket_thread_comments">> = {
          ticketThreadId: request.ticketThreadId,
          enduserId: request.enduserId,
          public: request.public,
        };

        return {
          _otherModels: {
            ticket_thread_comments: {
              getSome: {
                predicate: ({ queryKey }) => {
                  const queryRequest = extractRequestFromQueryKey(queryKey);
                  return modelMatchesGetSomeRequest(partialCommentFromRequest, queryRequest);
                },
                updater: (oldData: InfiniteData<TellescopeModelFromName<"ticket_thread_comments">[]> | undefined) => {
                  if (!oldData) return oldData;
                  return appendToInfiniteDataList({
                    infiniteData: oldData,
                    item: optimisticComment,
                  });
                },
              },
            },
          },
        };
      }

      // ---------------------------------------------------------------
      // onSuccess path — invalidate only (no updaters)
      // ---------------------------------------------------------------
      if (!response.success) return {};

      if (request.ticketThreadId) {
        // Adding a comment to an existing thread — invalidate matching queries
        const partialCommentFromRequest: Partial<TellescopeModelFromName<"ticket_thread_comments">> = {
          ticketThreadId: request.ticketThreadId,
          html: request.html_body,
          public: request.public,
          enduserId: request.enduserId,
        };

        return {
          getSome: {
            predicate: ({ queryKey }) => {
              const queryRequest = extractRequestFromQueryKey(queryKey);
              return modelMatchesGetSomeRequest(
                { id: request.ticketThreadId } as Partial<TellescopeModelFromName<"ticket_threads">>,
                queryRequest
              );
            },
            delay: DELAY,
          },
          getOne: {
            request: { id: request.ticketThreadId },
            delay: DELAY,
          },
          _otherModels: {
            ticket_thread_comments: {
              getSome: {
                predicate: ({ queryKey }) => {
                  const queryRequest = extractRequestFromQueryKey(queryKey);
                  return modelMatchesGetSomeRequest(partialCommentFromRequest, queryRequest);
                },
                delay: DELAY,
              },
            },
            inbox_threads: {
              load_threads: {
                predicate: ({ queryKey }) =>
                  zendeskThreadMatchesLoadThreadsQuery(request.enduserId, extractRequestFromQueryKey(queryKey)),
                request: { autobuild: true },
                exact: false,
                delay: DELAY,
              },
            },
          },
        };
      }

      // Creating a new thread — invalidate matching ticket_threads, comment, and inbox queries
      const partialThreadFromRequest: Partial<TellescopeModelFromName<"ticket_threads">> = {
        enduserId: request.enduserId,
      };

      const partialCommentFromRequest: Partial<TellescopeModelFromName<"ticket_thread_comments">> = {
        enduserId: request.enduserId,
        public: request.public,
      };

      return {
        getSome: {
          predicate: ({ queryKey }) => {
            const queryRequest = extractRequestFromQueryKey(queryKey);
            return modelMatchesGetSomeRequest(partialThreadFromRequest, queryRequest);
          },
          delay: DELAY,
        },
        _otherModels: {
          ticket_thread_comments: {
            getSome: {
              predicate: ({ queryKey }) => {
                const queryRequest = extractRequestFromQueryKey(queryKey);
                return modelMatchesGetSomeRequest(partialCommentFromRequest, queryRequest);
              },
              delay: DELAY,
            },
          },
          inbox_threads: {
            load_threads: {
              predicate: ({ queryKey }) =>
                zendeskThreadMatchesLoadThreadsQuery(request.enduserId, extractRequestFromQueryKey(queryKey)),
              request: { autobuild: true },
              exact: false,
              delay: DELAY,
            },
          },
        },
      };
    },
  };
